import React, { createContext } from 'react';
import { logError } from '@utils/usefulFuncs';

const defaultState = {
  isOpen: false,
  question: null,
  answer: null,
  reasoning: null,
  messages: [],
  isStreaming: false,
  error: null,
  openChat: () => logError('openChat not initialized'),
  closeChat: () => logError('closeChat not initialized'),
  addUserMessage: () => logError('addUserMessage not initialized'),
  addAssistantMessage: () => logError('addAssistantMessage not initialized'),
  updateLastAssistantMessage: () => logError('updateLastAssistantMessage not initialized'),
  finalizeLastAssistantMessage: () => logError('finalizeLastAssistantMessage not initialized'),
  setIsStreaming: () => logError('setIsStreaming not initialized'),
  setError: () => logError('setError not initialized'),
};

export const ReasoningChatContext = createContext(defaultState);

export const ReasoningChatProvider = ({ children }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [sourceData, setSourceData] = React.useState({ question: null, answer: null, reasoning: null });
  const [messages, setMessages] = React.useState([]);
  const [isStreaming, setIsStreaming] = React.useState(false);
  const [error, setError] = React.useState(null);

  const openChat = React.useCallback((question, answer, reasoning) => {
    setSourceData({ question, answer, reasoning });
    setIsOpen(true);
    setMessages([
      {
        role: 'assistant',
        text: '',
        answer: answer || '',
        reasoning: reasoning || '',
        isOriginal: true,
      },
    ]);
    setError(null);
    setIsStreaming(false);
  }, []);

  const closeChat = React.useCallback(() => {
    setIsOpen(false);
    setMessages([]);
    setError(null);
    setIsStreaming(false);
  }, []);

  const addUserMessage = React.useCallback((text) => {
    setMessages((prev) => [...prev, { role: 'user', text }]);
  }, []);

  const addAssistantMessage = React.useCallback(() => {
    setMessages((prev) => [...prev, { role: 'assistant', text: '', answer: '', reasoning: '' }]);
  }, []);

  const updateLastAssistantMessage = React.useCallback((rawText, parsed) => {
    setMessages((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last?.role === 'assistant') {
        next[next.length - 1] = {
          ...last,
          text: rawText,
          answer: parsed?.answer || last.answer,
          reasoning: parsed?.reasoning || last.reasoning,
        };
      }
      return next;
    });
  }, []);

  const finalizeLastAssistantMessage = React.useCallback((answer, reasoning) => {
    setMessages((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last?.role === 'assistant') {
        next[next.length - 1] = { ...last, answer: answer || last.answer, reasoning: reasoning || last.reasoning };
      }
      return next;
    });
  }, []);

  const value = React.useMemo(
    () => ({
      isOpen,
      question: sourceData.question,
      answer: sourceData.answer,
      reasoning: sourceData.reasoning,
      messages,
      isStreaming,
      error,
      openChat,
      closeChat,
      addUserMessage,
      addAssistantMessage,
      updateLastAssistantMessage,
      finalizeLastAssistantMessage,
      setIsStreaming,
      setError,
    }),
    [
      isOpen,
      sourceData,
      messages,
      isStreaming,
      error,
      openChat,
      closeChat,
      addUserMessage,
      addAssistantMessage,
      updateLastAssistantMessage,
      finalizeLastAssistantMessage,
    ]
  );

  return <ReasoningChatContext.Provider value={value}>{children}</ReasoningChatContext.Provider>;
};
